import ModalComponent from "components/common/Modal/Modal";
import { useContext, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { VscCloudUpload } from "react-icons/vsc";
import { useNavigate } from "react-router";
import { v4 } from "uuid";
import UserContext from "../../../../Context/UserProvider";
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import { useApiService } from "../../../../services/ApiService/ApiService";
import toast from 'react-hot-toast';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';

function UploadForm(props: any) {
  const { userInfo } = useContext(UserContext);
  const { fetchWithAuth } = useApiService();
  const theme = useTheme();
  const navigate = useNavigate();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  function openModal() {
    setIsOpen(true);
  }
  
  function closeModal() {
    setIsOpen(false);
  }

  const [fileContent, setFileContent] = useState([{ fileName: "", fileSize: 0, file: File, id: 0 }]);
  const [hoverZone, setHoverZone] = useState(false);

  const onDragEnter = (e: any): void => {
    e.preventDefault();
    setHoverZone(true);
  };
  
  const onDragLeave = (e: any): void => {
    e.preventDefault();
    setHoverZone(false);
  };

  const toBase64 = (file: any) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  function addNewFile(currentFiles: [any], newFile: any) {
    let result = [] as any;
    for (let i = 0; i < newFile?.length; i++) {
      if (
        currentFiles
          ?.map((e: any) => {
            return e.fileName;
          })
          .indexOf(newFile[i].fileName) < 0
      ) {
        result.push(newFile[i]);
      }
    }
    return result;
  }

  const converFileBase64 = async (file: any) => {
    let files_list_arr = [];
    for (let i = 0; i < file.length; i++) {
      let current_file: any = await toBase64(file[i].file);
      files_list_arr.push({
        originalFilename: file[i].fileName,
        encodedFile: current_file.slice(21),
      });
    }
    return files_list_arr;
  };

  const submitForm = async (fileContent: any) => {
    setUploading(true);
    
    //File preparation
    let fileToUpload = addNewFile([] as any, fileContent);
    if (fileToUpload.length <= 0) {
      toast.error("File already been upload");
      removeFile();
      setUploading(false);
      return;
    }
    removeFile();

    try {
      const bodyData = {
        userId: userInfo.uuid,
        businessUuid: userInfo.businessUuid,
        fileList: JSON.stringify(await converFileBase64(fileToUpload))
      };

      const response = await fetchWithAuth('/infrastructure/dwelling/list/import', {
        method: 'POST',
        body: JSON.stringify(bodyData)
      });

      if (!response.ok) {
        const res_obj = await response.json();
        throw new Error(res_obj.notifications?.[0]?.code || 'Upload failed');
      }

      toast.success("File finished uploading");
      openModal();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error instanceof Error ? error.message : "There is an error during upload");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (): void => {
    setFileContent([{ fileName: "", fileSize: 0, file: File, id: 0 }]);
  };

  const changeHandler = (e: any) => {
    setHoverZone(false);
    let i = 0;
    let files_arr: any[] = [];
    Array.from(e.target.files)
      .filter((file: any) => file.type === "text/csv" || file.type === "application/zip"|| file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
      .forEach((file: any) => {
        files_arr.push({
          fileName: file.name,
          fileSize: file.size,
          file: file,
          id: i,
        });
        i++;
      });
    setFileContent(files_arr as any);
    submitForm(files_arr);
  };

  const handleDrop = (e: any) => {
    let i = 0;
    let files_list: any[] = [];
    setHoverZone(false);
    e.preventDefault();
    Array.from(e.dataTransfer.files)
      .filter((file: any) => file.type === "text/csv" || file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
      .forEach(async (file: any) => {
        files_list.push({
          fileName: file.name as string,
          fileSize: file.size as number,
          file: file as any,
          id: i,
        });
        setFileContent(files_list as any);
        i++;
      });
  };

  return (
    <Box sx={{ p: 3 }}>
      <ModalComponent modalIsOpen={[modalIsOpen, setIsOpen]}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={closeModal} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            textAlign: 'center', 
            alignItems: 'center', 
            gap: 3 
          }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Locations uploaded successfully
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
              Click{" "}
              <Button
                variant="text"
                onClick={() =>
                  navigate("/plan/business/locations", {
                    state: { showCategory: [false, true] },
                  })
                }
                sx={{ 
                  textDecoration: 'underline', 
                  color: theme.palette.primary.main,
                  p: 0,
                  minWidth: 'auto',
                  textTransform: 'none'
                }}
              >
                here
              </Button>{" "}
              to see the list of locations
            </Typography>
          </Box>
        </Box>
      </ModalComponent>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Upload files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Files must be in xlsx format.
        </Typography>
      </Box>

      <Paper
        elevation={hoverZone ? 8 : 2}
        sx={{
          border: `2px dashed ${hoverZone ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3)}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: hoverZone ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          }
        }}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        {uploading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body1">Uploading files...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
            <Typography variant="body1">
              Drag & Drop your file(s) here or
            </Typography>
            <label>
              <input
                type="file"
                style={{ display: 'none' }}
                name="file"
                onChange={changeHandler}
                accept=".xlsx,.csv,.zip"
                multiple
              />
              <Button
                variant="outlined"
                component="span"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Browse Files
              </Button>
            </label>
          </Box>
        )}
      </Paper>

      {fileContent[0].fileName.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Selected files: {fileContent.map(f => f.fileName).join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default UploadForm;
